import clsx from 'clsx';
import { usePerfSampler } from '@/store/perfSampler';

interface Props {
  serial: string;
  /** Compact = corner pill (for FloatingMirror); full = inline badge with label. */
  variant?: 'compact' | 'full';
  className?: string;
}

/**
 * Tiny live FPS readout. Subscribes only to the last sample of the device,
 * so it does not re-render when historical samples change.
 *
 * Colour code:
 *   ≥ 50 fps  green   smooth
 *   30 – 50   amber   noticeable
 *   < 30      red     jank
 *   undefined slate   no signal
 */
export function FpsBadge({ serial, variant = 'compact', className }: Props) {
  const fps = usePerfSampler((s) => {
    const arr = s.byserial[serial]?.samples;
    if (!arr || arr.length === 0) return undefined;
    // Walk back up to 5 samples to find the most recent FPS reading. Some
    // sampling intervals (5s thermal) interleave with no-FPS samples, and
    // gfxinfo only emits once every ~2s.
    for (let i = arr.length - 1; i >= Math.max(0, arr.length - 5); i--) {
      if (arr[i].fps !== undefined) return arr[i].fps;
    }
    return undefined;
  });

  const color =
    fps === undefined
      ? 'bg-slate-500/70 text-white'
      : fps >= 50
      ? 'bg-emerald-500/90 text-white'
      : fps >= 30
      ? 'bg-amber-500/90 text-white'
      : 'bg-rose-500/95 text-white';

  if (variant === 'compact') {
    return (
      <div
        className={clsx(
          'pointer-events-none select-none rounded-md px-1.5 py-0.5 text-[10px] font-mono shadow-sm',
          color,
          className
        )}
        title={fps === undefined ? '等待 gfxinfo 数据' : `当前焦点窗口 FPS：${fps}`}
      >
        {fps !== undefined ? `${fps} fps` : '— fps'}
      </div>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-mono',
        color,
        className
      )}
    >
      <span>FPS</span>
      <span>{fps !== undefined ? fps : '—'}</span>
    </span>
  );
}
